import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogOverlay,
  AlertDialogTitle
} from './ui/dialog';
import { registerAppConfirmationHandler } from '../services/appConfirmation';
import '../styles/AppConfirmationDialog.css';

let nextConfirmationId = 0;

const AppConfirmationDialog = () => {
  const [requests, setRequests] = useState([]);
  const requestsRef = useRef(requests);

  useEffect(() => {
    requestsRef.current = requests;
  }, [requests]);

  useEffect(() => registerAppConfirmationHandler((options) => (
    new Promise((resolve) => {
      nextConfirmationId += 1;
      setRequests((current) => [
        ...current,
        { id: nextConfirmationId, options, resolve }
      ]);
    })
  )), []);

  useEffect(() => () => {
    requestsRef.current.forEach((request) => request.resolve(false));
  }, []);

  const activeRequest = requests[0] || null;

  const settleRequest = useCallback((requestId, result) => {
    setRequests((current) => {
      if (!current[0] || current[0].id !== requestId) return current;
      current[0].resolve(result);
      return current.slice(1);
    });
  }, []);

  if (!activeRequest) return null;

  const { id, options } = activeRequest;
  const isDanger = options.tone === 'danger';

  return (
    <AlertDialog.Root
      open
      onOpenChange={(open) => {
        if (!open) settleRequest(id, null);
      }}
    >
      <AlertDialog.Portal>
        <AlertDialogOverlay className="app-confirmation-overlay" />
        <AlertDialogContent className="app-confirmation-dialog" aria-modal="true">
          <div className={`app-confirmation-icon ${isDanger ? 'danger' : ''}`} aria-hidden="true">
            <AlertTriangle size={22} />
          </div>
          <div className="app-confirmation-copy">
            <AlertDialogTitle>{options.title}</AlertDialogTitle>
            {options.message && (
              <AlertDialogDescription>{options.message}</AlertDialogDescription>
            )}
          </div>
          <div className="app-confirmation-actions">
            <AlertDialog.Cancel asChild>
              <button
                type="button"
                className="app-confirmation-button secondary"
                onClick={() => settleRequest(id, false)}
              >
                {options.cancelLabel}
              </button>
            </AlertDialog.Cancel>
            <AlertDialog.Action asChild>
              <button
                type="button"
                className={`app-confirmation-button ${isDanger ? 'danger' : 'primary'}`}
                onClick={() => settleRequest(id, true)}
              >
                {options.confirmLabel}
              </button>
            </AlertDialog.Action>
          </div>
        </AlertDialogContent>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
};

export default AppConfirmationDialog;
